import React, { useCallback, useMemo } from "react";
import { AssociateDirectorInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addAssociateDirectorInfoRow,
  deleteAssociateDirectorInfoRow,
  selectFMR1DraftAssociateDirectorInfoRows,
  updateAssociateDirectorInfoRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type AssociateConcernDirectorDetailsTableHandle = {
  getDirtyRows: () => AssociateDirectorInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => AssociateDirectorInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: AssociateDirectorInfoRow[]) => void;
};

export interface AssociateConcernDirectorDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: AssociateDirectorInfoRow[];
}

const buildEmptyRow = (): AssociateDirectorInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const AssociateConcernDirectorDetailsTable: React.FC<AssociateConcernDirectorDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftAssociateDirectorInfoRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: AssociateDirectorInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updateAssociateDirectorInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addAssociateDirectorInfoRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updateAssociateDirectorInfoRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deleteAssociateDirectorInfoRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: keyof AssociateDirectorInfoRow) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updateAssociateDirectorInfoRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<AssociateDirectorInfoRow>[] = [
    {
      headerName: "Name of Associate Concern",
      field: "name_of_associate_concern",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_associate_concern ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_associate_concern"),
    },

    {
      headerName: "Sr. No. of Associate Concern",
      field: "sr_no_of_associate_concern",
      type: "number" as ColumnType,
      isEditable: true,
      getValue: (row) => row.sr_no_of_associate_concern ?? null,
      onChange: (rowIndex: number, value: number | null) =>
        handleChange(rowIndex, value, "sr_no_of_associate_concern"),
      headerClassName: "min-w-[200px]",
    },

    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      isEditable: false,
      className: "w-[60px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Name of Director",
      field: "name_of_director",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_director ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_director"),
    },

    {
      headerName: "PAN",
      field: "pan",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.pan ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "pan"),
    },

    {
      headerName: "DIN",
      field: "din",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.din ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "din"),
    },

    {
      headerName: "Director / Proprietor Address",
      field: "director_proprietor_address",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.director_proprietor_address ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "director_proprietor_address"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Associate Concern Director/ Proprietor Details"}
    />
  );
};

AssociateConcernDirectorDetailsTable.displayName = "AssociateConcernDirectorDetailsTable";
